import React from "react";

import groupBy from "lodash/groupBy";
import sortBy from "lodash/sortBy";
import toPairs from "lodash/toPairs";
import moment, { Moment } from "moment-timezone";
import { Link } from "react-router-dom";
import Color from "color";

import PanelSection from "ui/PanelSection";
import TimeBucketBar from "features/Logs/TimeBucketBar";
import ShowFlash from "ui/ShowFlash";
import Button from "ui/Button";
import { ExpandToggle } from "ui/Icons";
import { useRoutes } from "utils/routes";
import { formatBytes } from "utils/format";
import { useUserPreferences } from "utils/hooks";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSearchPlus } from "@fortawesome/pro-regular-svg-icons";

import directory from "../../../../../docs/directory.json";

import { logStatsBucketMinutes } from "./buckets";
import type { LogStatsViewServerFragment } from "./gql/LogStatsView.v2.generated";

type LogCategory = keyof typeof directory.logCategories;
type LogStatsValue = {
  timebucket: number;
  classification: string;
  count: number;
};

type LogClassificationStatsType = [string, LogStatsValue[]];
type LogClassificationStatsCategoryType = [
  string,
  LogClassificationStatsType[],
];

export function LogStatsView({
  server,
  ...barsProps
}: {
  server: LogStatsViewServerFragment;
  serverId: string;
  databaseId?: string;
  selectedClassification: string | null | undefined;
  highlightedTime: moment.Moment | null | undefined;
  startTs: number;
  endTs: number;
}) {
  const stats = server.logStats.map((s) => ({
    timebucket: moment(s.bucketStart).unix(),
    classification: s.classification ?? "unknown",
    count: s.count,
  }));
  return (
    <>
      <LogQuotaWarning bytesOverQuota={server.logQuota.overQuotaBytes} />
      <LogStatsBars stats={stats} {...barsProps} />
    </>
  );
}

function LogStatsBars({
  databaseId,
  serverId,
  stats,
  startTs,
  endTs,
  selectedClassification,
  highlightedTime,
}: {
  serverId: string;
  databaseId?: string;
  selectedClassification: string | null | undefined;
  highlightedTime: moment.Moment | null | undefined;
  stats: LogStatsValue[];
  startTs: number;
  endTs: number;
}) {
  const [expandedState, setExpandedState] = useUserPreferences("logInsights");

  const logStats = stats;
  if (!logStats || logStats.length === 0) {
    return (
      <PanelSection>
        <strong>Error:</strong> No logs were received
        {databaseId ? " for this database" : ""} in the selected timeframe.
      </PanelSection>
    );
  }

  if (selectedClassification) {
    const filtered = logStats.filter(
      (l) => l.classification == selectedClassification,
    );
    return (
      <ClassificationDetails
        logStats={filtered}
        serverId={serverId}
        databaseId={databaseId}
        classification={selectedClassification}
        highlightedTime={highlightedTime}
        endTs={endTs}
        startTs={startTs}
      />
    );
  }

  const classificationDetailsKeys = Object.keys(directory.logClassifications);
  const categoriesKeys = Object.keys(directory.logCategories);
  let logStatsByClassification: LogClassificationStatsType[] = toPairs(
    groupBy(logStats, (l) => l.classification),
  );

  logStatsByClassification = sortBy(
    logStatsByClassification,
    ([classification]): number => {
      const idx = classificationDetailsKeys.indexOf(classification);
      return idx === -1 ? Number.MAX_VALUE : idx;
    },
  );

  let classificationStatsByCategory: LogClassificationStatsCategoryType[] =
    toPairs(
      groupBy(
        logStatsByClassification,
        (l) =>
          directory.logClassifications[
            l[0] as keyof typeof directory.logClassifications
          ].category,
      ),
    );

  classificationStatsByCategory = sortBy(
    classificationStatsByCategory,
    ([category]): number => {
      const idx = categoriesKeys.indexOf(category);
      return idx === -1 ? Number.MAX_VALUE : idx;
    },
  );

  const expandedCategories =
    expandedState.expandedCategories ??
    (["app-errors", "locks", "constraint-violations"] satisfies LogCategory[]);

  return (
    <div className="py-[5px] px-[8px] border-b border-[#eee] grid grid-cols-[180px_1fr]">
      {classificationStatsByCategory.map(([category, categoryStats]) => {
        const isExpanded = expandedCategories.some((cat) => cat === category);
        function handleExpandedToggled() {
          const existingExpanded = expandedCategories;
          const alreadyExpanded = existingExpanded.some(
            (cat) => cat == category,
          );
          const newExpanded = alreadyExpanded
            ? existingExpanded.filter((cat) => cat != category)
            : existingExpanded.concat(category);
          setExpandedState({
            ...expandedState,
            expandedCategories: newExpanded,
          });
        }
        return (
          <CategoryLogStats
            key={category}
            category={category as LogCategory}
            highlightedTime={highlightedTime}
            endTs={endTs}
            startTs={startTs}
            serverId={serverId}
            databaseId={databaseId}
            categoryStats={categoryStats}
            isExpanded={isExpanded}
            onExpandedToggled={handleExpandedToggled}
          />
        );
      })}
    </div>
  );
}

function LogQuotaWarning({ bytesOverQuota }: { bytesOverQuota: number }) {
  if (bytesOverQuota === 0) {
    return null;
  }
  return (
    <ShowFlash
      level="alert"
      msg={
        <>
          This server exceeded its log rate limit by{" "}
          {formatBytes(bytesOverQuota)} in the selected time period; some log
          lines were dropped.{" "}
          <a
            target="_blank"
            rel="noopener"
            href="https://pganalyze.com/docs/log-insights/limits"
          >
            Learn more
          </a>
          .
        </>
      }
    />
  );
}

function CategoryLogStats({
  category,
  databaseId,
  serverId,
  highlightedTime,
  endTs,
  startTs,
  categoryStats,
  isExpanded,
  onExpandedToggled,
}: {
  category: LogCategory;
  databaseId: string;
  serverId: string;
  highlightedTime: moment.Moment;
  endTs: number;
  startTs: number;
  categoryStats: LogClassificationStatsType[];
  isExpanded: boolean;
  onExpandedToggled: () => void;
}) {
  const categoryTitle = directory.logCategories[category];
  return (
    <>
      <Button
        className="flex items-center gap-1.5 mt-4 first-of-type:mt-0"
        bare
        onClick={onExpandedToggled}
      >
        <ExpandToggle className="w-3" expanded={isExpanded} />
        <div className="font-bold whitespace-nowrap">{categoryTitle}</div>
      </Button>
      {isExpanded ? (
        <>
          <div />
          {categoryStats.map(([classification, stats]) => (
            <ClassificationLogStats
              key={classification}
              category={category}
              stats={stats}
              classification={classification}
              databaseId={databaseId}
              serverId={serverId}
              highlightedTime={highlightedTime}
              endTs={endTs}
              startTs={startTs}
            />
          ))}
        </>
      ) : (
        <CategoryLogStatsSummary
          className="mt-4 first-of-type:mt-0"
          category={category}
          categoryStats={categoryStats}
          databaseId={databaseId}
          serverId={serverId}
          highlightedTime={highlightedTime}
          endTs={endTs}
          startTs={startTs}
        />
      )}
    </>
  );
}

function CategoryLogStatsSummary({
  className,
  databaseId,
  serverId,
  highlightedTime,
  endTs,
  startTs,
  category,
  categoryStats,
}: {
  className?: string;
  databaseId: string;
  serverId: string;
  highlightedTime: moment.Moment;
  endTs: number;
  startTs: number;
  category: LogCategory;
  categoryStats: LogClassificationStatsType[];
}) {
  const categoryTitle = directory.logCategories[category];
  const timebucketIndexes = new Map<number, number>();
  const summaryStats = categoryStats
    .flatMap(([_classification, stats]) => stats)
    .reduce<LogStatsValue[]>((accum, stats) => {
      const { timebucket } = stats;
      if (timebucketIndexes.has(timebucket)) {
        const bucketIdx = timebucketIndexes.get(timebucket);
        accum[bucketIdx].count += stats.count;
      } else {
        const newBucketIdx = accum.length;
        timebucketIndexes.set(timebucket, newBucketIdx);
        accum.push({
          timebucket: timebucket,
          classification: `${categoryTitle} Events`,
          count: stats.count,
        });
      }
      return accum;
    }, []);
  const maxCount = summaryStats.reduce<number>(
    (max, curr) => Math.max(max, curr.count),
    0,
  );
  return (
    <Stats
      className={className}
      databaseId={databaseId}
      serverId={serverId}
      highlightedTime={highlightedTime}
      endTs={endTs}
      startTs={startTs}
      category={category}
      stats={summaryStats}
      maxCount={maxCount}
    />
  );
}

// From 10-color set in https://mk.bcgsc.ca/colorblind/distinct.colors.mhtml
const CATEGORY_COLORS: Record<LogCategory, string> = {
  server: "#9c355f",
  connections: "#446728",
  "wal-checkpoints": "#76b056",
  maintenance: "#00a5a5",
  locks: "#cc76c0",
  statements: "#da7457",
  standby: "#668fd3",
  "constraint-violations": "#5f4799",
  "app-errors": "#8a4719",
  unknown: "#c39643",
};

function getCategoryColor(category: LogCategory): Color {
  return new Color(CATEGORY_COLORS[category]);
}

function ClassificationLogStats({
  category,
  stats,
  classification,
  databaseId,
  serverId,
  endTs,
  startTs,
  highlightedTime,
}: {
  category: LogCategory;
  stats: LogStatsValue[];
  classification: string;
  databaseId: string;
  serverId: string;
  endTs: number;
  startTs: number;
  highlightedTime: Moment;
}) {
  const { databaseLogs, serverLogs } = useRoutes();
  const maxCount = Math.max(...stats.map((g) => g.count));
  const details =
    directory.logClassifications[
      classification as keyof typeof directory.logClassifications
    ];

  let classificationPath = "";
  if (databaseId) {
    classificationPath = databaseLogs(databaseId, classification);
  } else if (serverId) {
    classificationPath = serverLogs(serverId, classification);
  }

  return (
    <>
      <div className="pr-[5px] h-5 relative cursor-pointer">
        <Link
          to={classificationPath}
          className="text-inherit hover:text-inherit focus:text-inherit"
        >
          <span
            title="Click for more details"
            className="group overflow-hidden inline-block text-ellipsis whitespace-nowrap absolute top-0 left-0 max-w-[180px] hover:top-[-6px] hover:left-[-11px] hover:max-w-none hover:bg-[rgba(255,255,255,0.9)] hover:px-[10px] hover:py-[5px] hover:border hover:border-[#ddd] hover:shadow-sm hover:z-[2] hover:rounded-sm"
          >
            <FontAwesomeIcon
              icon={faSearchPlus}
              className="text-[var(--color-gray-700)] hover:text-[var(--color-gray-950)]"
            />{" "}
            {details.title}
            <span className="hidden group-hover:inline">
              {` (${classification})`}
            </span>
          </span>
        </Link>
      </div>
      <div className="h-5">
        <Stats
          serverId={serverId}
          databaseId={databaseId}
          category={category}
          stats={stats}
          maxCount={maxCount}
          highlightedTime={highlightedTime}
          endTs={endTs}
          startTs={startTs}
          link
        />
      </div>
    </>
  );
}

function Stats({
  className,
  category,
  stats,
  maxCount,
  highlightedTime,
  endTs,
  startTs,
  serverId,
  databaseId,
  link = false,
}: {
  className?: string;
  stats: LogStatsValue[];
  category: LogCategory;
  maxCount: number;
  highlightedTime: Moment;
  endTs: number;
  startTs: number;
  serverId: string;
  databaseId: string;
  link?: boolean;
}) {
  const { databaseLogs, serverLogs } = useRoutes();
  if (stats.length === 0) {
    return null;
  }
  const from = moment.unix(startTs);
  const to = moment.unix(endTs);
  const bucketSize = moment.duration(
    logStatsBucketMinutes(from, to),
    "minutes",
  );

  const linkable = link && (databaseId != null || serverId != null);
  const buckets = stats
    .filter((group) => group.timebucket >= startTs)
    .map((group) => {
      const start = moment.unix(group.timebucket);
      const focusTs = moment.min(start.clone().add(bucketSize), to).unix();
      const url =
        linkable &&
        (databaseId
          ? databaseLogs(databaseId, group.classification, focusTs)
          : serverLogs(serverId, group.classification, focusTs));
      return { start, value: group.count, url };
    });
  const representativeGroup = stats[0];
  const classification =
    representativeGroup.classification as keyof typeof directory.logClassifications;
  const details = directory.logClassifications[classification];
  const color = getCategoryColor(category);

  function TimeBucketTip({
    start,
    end,
    value,
  }: {
    start: Moment;
    end: Moment;
    value: number;
  }) {
    return (
      <div className="min-w-64">
        <strong>{value}</strong> &times;{" "}
        {details
          ? `${representativeGroup.classification}: ${details.title}`
          : representativeGroup.classification || "?"}
        <div className="text-[var(--color-gray-700)]">
          {start.format("MMM DD hh:mma")}
          &mdash;
          {end.format("hh:mma zz")}
        </div>
      </div>
    );
  }

  return (
    <TimeBucketBar
      className={className}
      from={from}
      to={to}
      highlight={highlightedTime}
      buckets={buckets}
      bucketSize={bucketSize}
      maxValue={maxCount}
      color={Color(color)}
      tip={TimeBucketTip}
    />
  );
}

function ClassificationDetails({
  logStats,
  classification,
  highlightedTime,
  endTs,
  startTs,
  serverId,
  databaseId,
}: {
  logStats: LogStatsValue[];
  classification: string;
  highlightedTime: Moment;
  endTs: number;
  startTs: number;
  serverId: string;
  databaseId: string;
}) {
  const category = directory.logClassifications[
    classification as keyof typeof directory.logClassifications
  ].category as LogCategory;
  const maxCount = Math.max(...logStats.map((g) => g.count));

  return (
    <PanelSection>
      <table className="w-full">
        <tbody>
          <tr>
            <td className="h-5">
              <Stats
                serverId={serverId}
                databaseId={databaseId}
                category={category}
                stats={logStats}
                maxCount={maxCount}
                highlightedTime={highlightedTime}
                endTs={endTs}
                startTs={startTs}
                link={false}
              />
            </td>
          </tr>
        </tbody>
      </table>
    </PanelSection>
  );
}
